/**
 * API client for all feature endpoints.
 * Uses REACT_APP_BACKEND_URL from environment.
 * Falls back gracefully if user is not authenticated.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const headers = { 'Content-Type': 'application/json' };

async function apiFetch(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    credentials: 'include',
    headers,
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'API request failed');
  }
  return res.json();
}

// Notifications
export const getNotifications = () => apiFetch('/api/notifications');
export const markNotificationRead = (id) => apiFetch(`/api/notifications/${id}/read`, { method: 'PUT' });
export const markAllNotificationsRead = () => apiFetch('/api/notifications/read-all', { method: 'POST' });
export const deleteNotification = (id) => apiFetch(`/api/notifications/${id}`, { method: 'DELETE' });

// Messages
export const getConversations = () => apiFetch('/api/conversations');
export const getMessages = (convId) => apiFetch(`/api/conversations/${convId}/messages`);
export const sendMessage = (convId, text) => apiFetch(`/api/conversations/${convId}/messages`, { method: 'POST', body: JSON.stringify({ text }) });

// Contracts
export const getContracts = () => apiFetch('/api/contracts');

// Time Entries
export const getTimeEntries = () => apiFetch('/api/time-entries');
export const createTimeEntry = (data) => apiFetch('/api/time-entries', { method: 'POST', body: JSON.stringify(data) });

// Surveys
export const getSurveys = () => apiFetch('/api/surveys');
export const submitSurvey = (id, answers) => apiFetch(`/api/surveys/${id}/submit`, { method: 'POST', body: JSON.stringify({ answers }) });

// Work Trials
export const getWorkTrials = () => apiFetch('/api/work-trials');
export const acceptTrial = (id) => apiFetch(`/api/work-trials/${id}/accept`, { method: 'POST' });

// Instant Offers
export const getInstantOffers = () => apiFetch('/api/instant-offers');
export const respondToOffer = (id, action) => apiFetch(`/api/instant-offers/${id}/respond`, { method: 'PUT', body: JSON.stringify({ action }) });

// Endorsements
export const getEndorsements = () => apiFetch('/api/endorsements');
export const requestEndorsement = (email, message) => apiFetch('/api/endorsements/request', { method: 'POST', body: JSON.stringify({ email, message }) });

// Background Check
export const getBackgroundCheck = () => apiFetch('/api/background-check');
export const submitBackgroundCheck = (data) => apiFetch('/api/background-check/submit', { method: 'POST', body: JSON.stringify(data) });

// Settings
export const getSettings = () => apiFetch('/api/settings');
export const updateSettings = (data) => apiFetch('/api/settings', { method: 'PUT', body: JSON.stringify(data) });

// Onboarding
export const getOnboardingProgress = () => apiFetch('/api/onboarding/progress');
export const updateOnboardingStep = (stepId) => apiFetch('/api/onboarding/step', { method: 'PUT', body: JSON.stringify({ step_id: stepId, completed: true }) });

// Taxes
export const getW9 = () => apiFetch('/api/taxes/w9');
export const submitW9 = (data) => apiFetch('/api/taxes/w9', { method: 'POST', body: JSON.stringify(data) });
export const getTaxDocuments = () => apiFetch('/api/taxes/documents');
export const getTaxPayments = () => apiFetch('/api/taxes/payments');

// Jobs
export const getJobs = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiFetch(`/api/jobs${query ? `?${query}` : ''}`);
};
export const getJobById = (id) => apiFetch(`/api/jobs/${id}`);
export const createJob = (data) => apiFetch('/api/jobs', { method: 'POST', body: JSON.stringify(data) });
export const updateJob = (id, data) => apiFetch(`/api/jobs/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteJob = (id) => apiFetch(`/api/jobs/${id}`, { method: 'DELETE' });

// Applications
export const submitApplication = (data) => apiFetch('/api/applications', { method: 'POST', body: JSON.stringify(data) });
export const getCandidateApplications = () => apiFetch('/api/applications/candidate');
export const getCompanyApplications = () => apiFetch('/api/applications/company');
export const updateApplicationStatus = (id, status) => apiFetch(`/api/applications/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });

// Profiles
export const getCandidateProfile = () => apiFetch('/api/profiles/candidate');
export const updateCandidateProfile = (data) => apiFetch('/api/profiles/candidate', { method: 'PUT', body: JSON.stringify(data) });
export const getCompanyProfile = () => apiFetch('/api/profiles/company');
export const updateCompanyProfile = (data) => apiFetch('/api/profiles/company', { method: 'PUT', body: JSON.stringify(data) });

// Application Signup
export const createApplicationSignup = (data) => apiFetch('/api/application-signup', { method: 'POST', body: JSON.stringify(data) });

// Auth
export const getMe = () => apiFetch('/api/auth/me');
